import { Link } from "react-router-dom";
import Seo, { orgSchema, breadcrumbSchema } from "@/components/Seo";
import { MaskedLines, FadeUp, ParallaxImage, Eyebrow, ArrowLink } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import { Accordion } from "@/components/Accordion";
import { SERVICES, PROJECTS, PROCESS, HOME_FAQS, IMAGES, SITE } from "@/data/content";

const Hero = () => (
    <section className="relative min-h-screen grid lg:grid-cols-12 bg-chalk" data-testid="hero">
        <div className="lg:col-span-7 flex flex-col justify-center px-6 md:px-12 lg:px-16 xl:px-24 pt-32 pb-16 lg:py-0">
            <FadeUp delay={0.1} y={16}>
                <Eyebrow>Dagaz Development — Oregon, USA</Eyebrow>
            </FadeUp>
            <MaskedLines
                lines={["Build what", "endures."]}
                as="h1"
                delay={0.25}
                className="mt-10 font-serif font-light text-[17vw] lg:text-[7.2vw] leading-[0.92] tracking-tight text-graphite"
                lineClassName=""
            />
            <FadeUp delay={0.7} className="mt-10 max-w-md">
                <p className="font-sans text-base md:text-lg leading-relaxed text-slate" data-testid="hero-subline">
                    {SITE.oneLiner}
                </p>
            </FadeUp>
            <FadeUp delay={0.85} className="mt-12 flex flex-wrap items-center gap-8">
                <Link
                    to="/start-a-project"
                    data-testid="hero-cta-start-project"
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-forest text-chalk font-sans text-xs font-semibold tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-graphite"
                >
                    Start a Project
                    <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5" aria-hidden="true">→</span>
                </Link>
                <ArrowLink to="/services" testid="hero-cta-explore">Explore Services</ArrowLink>
            </FadeUp>
            <FadeUp delay={1} className="mt-16 lg:mt-24 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-graphite/10 pt-6">
                {["Build / Renovate / Craft", "USA — Canada", "Audited Before Built"].map((t, i) => (
                    <span key={i} className="font-sans text-[10px] font-medium tracking-[0.28em] uppercase text-slate">{t}</span>
                ))}
            </FadeUp>
        </div>
        <div className="lg:col-span-5 relative flex h-[62vh] lg:h-auto">
            <ParallaxImage
                src={IMAGES.pavilions}
                alt="Twin cedar-clad pavilions set between tall pines — a Dagaz Development design study"
                className="flex-1"
                speed={8}
                eager
            />
            <div className="absolute inset-0 bg-graphite/15" aria-hidden="true" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 flex items-end justify-between bg-gradient-to-t from-graphite/60 to-transparent">
                <p className="font-sans text-[10px] font-medium tracking-[0.28em] uppercase text-chalk" data-testid="hero-image-caption">
                    Twin Pavilions — Design Study
                </p>
                <span className="w-10 h-10 border border-gold/70 flex items-center justify-center" aria-hidden="true">
                    <span className="w-1.5 h-1.5 bg-gold" />
                </span>
            </div>
        </div>
    </section>
);

const Intro = () => (
    <section className="px-6 md:px-12 lg:px-16 py-28 md:py-40 bg-chalk" data-testid="intro-section">
        <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-6">
                <Eyebrow className="mb-8">The Studio</Eyebrow>
                <MaskedLines
                    lines={["Considered from", "the ground up."]}
                    inView
                    className="font-serif font-light text-4xl md:text-6xl leading-[1.02] tracking-tight text-graphite"
                />
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex flex-col justify-end">
                <FadeUp>
                    <p className="font-sans text-base md:text-lg leading-relaxed text-graphite/80">
                        {SITE.entity}
                    </p>
                </FadeUp>
                <div className="mt-12 grid grid-cols-3 gap-6">
                    {[["09", "Service lines"], ["02", "Countries"], ["63", "States & provinces"]].map(([n, label], i) => (
                        <FadeUp key={i} delay={0.15 * i} className="border-t border-graphite/15 pt-4">
                            <p className="font-serif font-light text-4xl md:text-5xl text-graphite">{n}</p>
                            <p className="mt-2 font-sans text-[10px] font-medium tracking-[0.22em] uppercase text-slate">{label}</p>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </div>
    </section>
);

const tileLayout = [
    "lg:col-span-7 lg:row-span-2 min-h-[420px] lg:min-h-0",
    "lg:col-span-5 min-h-[220px]",
    "lg:col-span-5 min-h-[220px]",
    "lg:col-span-5 min-h-[300px]",
    "lg:col-span-7 min-h-[300px]",
    "lg:col-span-4 min-h-[240px]",
    "lg:col-span-4 min-h-[240px]",
    "lg:col-span-4 min-h-[240px]",
    "lg:col-span-12 min-h-[200px]",
];

const ServicesBento = () => (
    <section className="px-6 md:px-12 lg:px-16 py-28 md:py-36 bg-fog/40" data-testid="services-bento">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
                <Eyebrow className="mb-6">What We Do</Eyebrow>
                <MaskedLines
                    lines={["Nine disciplines.", "One accountable partner."]}
                    inView
                    className="font-serif font-light text-4xl md:text-6xl leading-[1.02] tracking-tight text-graphite"
                />
            </div>
            <ArrowLink to="/services" testid="bento-all-services">All Services</ArrowLink>
        </div>
        <div className="grid lg:grid-cols-12 gap-4 lg:auto-rows-[150px]">
            {SERVICES.map((s, i) => {
                const withImage = i === 0 || i === 3;
                const darkTile = i === 4;
                return (
                    <FadeUp key={s.slug} delay={0.05 * i} className={`col-span-1 ${tileLayout[i]}`}>
                        <Link
                            to={`/services/${s.slug}`}
                            data-testid={`service-tile-${s.slug}`}
                            className={`group relative flex flex-col justify-between h-full p-7 overflow-hidden border transition-colors duration-500 ${withImage ? "border-graphite/10" : darkTile ? "bg-forest border-forest" : "bg-chalk border-graphite/10 hover:border-gold/50"}`}
                        >
                            {withImage && (
                                <>
                                    <img
                                        src={s.image}
                                        alt={s.name}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.65,0,0.35,1)] group-hover:scale-105"
                                    />
                                    <span className="absolute inset-0 bg-graphite/55 transition-colors duration-700 group-hover:bg-graphite/40" aria-hidden="true" />
                                </>
                            )}
                            <span className={`relative font-sans text-[10px] font-medium tracking-[0.28em] ${withImage || darkTile ? "text-fog" : "text-slate"}`}>
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className="relative">
                                <span className={`block font-serif font-light leading-tight ${i === 0 ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"} ${withImage || darkTile ? "text-chalk" : "text-graphite"} group-hover:italic transition-all duration-500`}>
                                    {s.name}
                                </span>
                                <span className={`mt-3 block font-sans text-sm leading-relaxed max-w-sm ${withImage || darkTile ? "text-fog" : "text-slate"}`}>
                                    {s.short}
                                </span>
                                <span className={`mt-5 inline-flex items-center gap-2 font-sans text-[10px] font-semibold tracking-[0.24em] uppercase ${withImage || darkTile ? "text-gold" : "text-forest"}`}>
                                    Explore
                                    <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5" aria-hidden="true">→</span>
                                </span>
                            </span>
                        </Link>
                    </FadeUp>
                );
            })}
        </div>
    </section>
);

const Process = () => (
    <section className="bg-graphite bg-noise px-6 md:px-12 lg:px-16 py-28 md:py-40" data-testid="process-section">
        <Eyebrow dark className="mb-8">How It Works</Eyebrow>
        <div className="flex flex-wrap items-end justify-between gap-8 mb-20">
            <MaskedLines
                lines={["A clear path, from", "first sketch to keys."]}
                inView
                className="font-serif font-light text-4xl md:text-6xl leading-[1.02] tracking-tight text-chalk"
            />
            <ArrowLink to="/how-it-works" dark testid="process-full-link">The Full Process</ArrowLink>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-14 border-t border-chalk/15 pt-14">
            {PROCESS.map((step, i) => (
                <FadeUp key={step.n} delay={0.1 * i}>
                    <p className="font-serif font-light text-6xl text-chalk/10">{step.n}</p>
                    <h3 className="mt-5 font-serif text-2xl text-chalk">{step.title}</h3>
                    <p className="mt-4 font-sans text-sm leading-relaxed text-fog">{step.body}</p>
                    <p className="mt-5 font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-gold">{step.duration}</p>
                </FadeUp>
            ))}
        </div>
    </section>
);

const ProjectsGallery = () => (
    <section className="px-6 md:px-12 lg:px-16 py-28 md:py-40 bg-chalk" data-testid="projects-gallery">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
                <Eyebrow className="mb-6">Selected Work</Eyebrow>
                <MaskedLines
                    lines={["Quiet form.", "Lasting character."]}
                    inView
                    className="font-serif font-light text-4xl md:text-6xl leading-[1.02] tracking-tight text-graphite"
                />
            </div>
            <ArrowLink to="/projects" testid="gallery-all-projects">All Projects</ArrowLink>
        </div>
        <div className="grid lg:grid-cols-12 gap-6">
            {[
                { p: PROJECTS[1], cls: "lg:col-span-7", h: "h-[380px] lg:h-[560px]" },
                { p: PROJECTS[0], cls: "lg:col-span-5", h: "h-[380px] lg:h-[560px]" },
                { p: PROJECTS[2], cls: "lg:col-span-5", h: "h-[380px] lg:h-[480px]" },
                { p: PROJECTS[3], cls: "lg:col-span-7", h: "h-[380px] lg:h-[480px]" },
            ].map(({ p, cls, h }, i) => (
                <FadeUp key={p.slug} delay={0.08 * i} className={cls}>
                    <Link to={`/projects/${p.slug}`} data-testid={`project-card-${p.slug}`} className="group block">
                        <div className={`relative overflow-hidden ${h}`}>
                            <img
                                src={p.image}
                                alt={`${p.name} — ${p.type}, ${p.location}`}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.6s] ease-[cubic-bezier(0.65,0,0.35,1)] group-hover:scale-[1.06]"
                            />
                            <span className="absolute top-5 left-5 px-3 py-1.5 bg-graphite/70 backdrop-blur-sm font-sans text-[9px] font-medium tracking-[0.24em] uppercase text-chalk">
                                {p.status}
                            </span>
                        </div>
                        <div className="mt-5 flex items-baseline justify-between gap-4 border-t border-graphite/10 pt-4">
                            <h3 className="font-serif font-light text-2xl md:text-3xl text-graphite group-hover:italic transition-all duration-500">{p.name}</h3>
                            <p className="font-sans text-[10px] font-medium tracking-[0.2em] uppercase text-slate text-right shrink-0">
                                {p.type} — {p.location}
                            </p>
                        </div>
                    </Link>
                </FadeUp>
            ))}
        </div>
    </section>
);

const FaqPreview = () => (
    <section className="px-6 md:px-12 lg:px-16 py-28 md:py-36 bg-fog/40" data-testid="faq-preview">
        <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-4">
                <Eyebrow className="mb-6">Questions</Eyebrow>
                <MaskedLines
                    lines={["Answered", "plainly."]}
                    inView
                    className="font-serif font-light text-4xl md:text-5xl leading-[1.02] tracking-tight text-graphite"
                />
                <FadeUp delay={0.3} className="mt-10">
                    <ArrowLink to="/faq" testid="faq-preview-link">All Questions</ArrowLink>
                </FadeUp>
            </div>
            <div className="lg:col-span-8">
                <FadeUp>
                    <Accordion items={HOME_FAQS} testidPrefix="home-faq" />
                </FadeUp>
            </div>
        </div>
    </section>
);

const FinalCta = () => (
    <section className="relative bg-forest bg-noise px-6 md:px-12 lg:px-16 py-32 md:py-44 overflow-hidden" data-testid="final-cta">
        <p className="pointer-events-none select-none absolute -bottom-10 left-0 font-serif font-light text-[26vw] leading-none text-outline opacity-60" aria-hidden="true">
            DAGAZ
        </p>
        <div className="relative max-w-4xl">
            <Eyebrow dark className="mb-8">Begin</Eyebrow>
            <MaskedLines
                lines={["Start with", "a conversation."]}
                inView
                className="font-serif font-light text-5xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tight text-chalk"
            />
            <FadeUp delay={0.3} className="mt-10 max-w-lg">
                <p className="font-sans text-base md:text-lg leading-relaxed text-fog">
                    Tell us about your land, your building, or the one you are still imagining. Every brief is read personally and answered within two business days.
                </p>
            </FadeUp>
            <FadeUp delay={0.45} className="mt-12 flex flex-wrap items-center gap-8">
                <Link
                    to="/start-a-project"
                    data-testid="final-cta-start-project"
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-chalk text-graphite font-sans text-xs font-semibold tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-gold"
                >
                    Start a Project
                    <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5" aria-hidden="true">→</span>
                </Link>
                <a href={`mailto:${SITE.email}`} data-testid="final-cta-email" className="link-rule font-sans text-xs font-medium tracking-[0.22em] uppercase text-chalk">
                    {SITE.email}
                </a>
            </FadeUp>
        </div>
    </section>
);

export default function Home() {
    return (
        <>
            <Seo
                title="Dagaz Development — Build What Endures | Custom Homes, US & Canada"
                description="Oregon design-and-build company delivering custom homes, reconstructions and commercial projects with licensed local builders across the US and Canada."
                path="/"
                image={IMAGES.pavilions}
                jsonLd={[orgSchema, breadcrumbSchema([{ name: "Home", path: "/" }]), {
                    "@context": "https://schema.org",
                    "@type": "ProfessionalService",
                    name: SITE.name,
                    email: SITE.email,
                    address: { "@type": "PostalAddress", addressRegion: "OR", addressCountry: "US" },
                    areaServed: ["United States", "Canada"],
                    slogan: SITE.tagline,
                }]}
            />
            <Hero />
            <EditorialMarquee />
            <Intro />
            <ServicesBento />
            <Process />
            <ProjectsGallery />
            <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-chalk border-y border-graphite/10" data-testid="financing-teaser">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    <FadeUp className="lg:col-span-4">
                        <Eyebrow className="mb-6">Financing</Eyebrow>
                        <p className="font-serif font-light text-7xl md:text-8xl text-graphite leading-none">$10M</p>
                        <p className="mt-3 font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-slate max-w-[240px]">
                            Project financing ceiling — qualifying builds
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-7 lg:col-start-6">
                        <h2 className="font-serif font-light text-3xl md:text-5xl leading-tight tracking-tight text-graphite">
                            Financing that fits the build, arranged with partner lenders.
                        </h2>
                        <p className="mt-6 font-sans text-base leading-relaxed text-graphite/75 max-w-xl">
                            Construction loans, construction-to-permanent structures, land as down payment — and organized financing for developer projects up to $10 million USD.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            {["Construction loans", "Construction-to-permanent", "Land as down payment", "Developer facilities"].map((t) => (
                                <span key={t} className="px-4 py-2 border border-graphite/15 font-sans text-[10px] font-medium tracking-[0.2em] uppercase text-slate">
                                    {t}
                                </span>
                            ))}
                        </div>
                        <ArrowLink to="/financing" testid="financing-teaser-link" className="mt-10">How Financing Works</ArrowLink>
                    </FadeUp>
                </div>
            </section>
            <section className="px-6 md:px-12 lg:px-16 py-8 bg-chalk" data-testid="partner-strip">
                <FadeUp className="flex flex-wrap items-center justify-between gap-6 border-b border-graphite/10 pb-14">
                    <p className="font-serif font-light text-2xl md:text-3xl text-graphite max-w-2xl">
                        Delivered with licensed local builders, architects, suppliers and lenders — <span className="italic">one accountable partner.</span>
                    </p>
                    <ArrowLink to="/partners" testid="partner-strip-link">Work With Dagaz</ArrowLink>
                </FadeUp>
            </section>
            <FaqPreview />
            <FinalCta />
        </>
    );
}
